import React, { useEffect, createContext, useContext, useState } from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";

// Context
import { CurrentLanguage } from "./current-language";

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `rinck_shopify_cart_id`;

// Get store domain from environment
const storeDomain = process.env.GATSBY_SHOPIFY_STORE_URL;

// Get token from environment
const storefrontToken = process.env.GATSBY_STOREFRONT_ACCESS_TOKEN;

if (!storefrontToken) {
  console.error(
    "Storefront API token is missing. Please check your environment variables."
  );
  throw new Error(
    "GATSBY_STOREFRONT_ACCESS_TOKEN environment variable is not set"
  );
}

// Helper function to transform GraphQL cart data to expected format
const transformCartData = (cartData) => {
  if (!cartData) return { lineItems: [] };

  const transformed = {
    ...cartData,
    id: cartData.id,
    webUrl: cartData.checkoutUrl,
    subtotalPriceV2: cartData.cost?.subtotalAmount || {
      amount: "0.00",
      currencyCode: "EUR",
    },
    totalPriceV2: cartData.cost?.totalAmount || {
      amount: "0.00",
      currencyCode: "EUR",
    },
    lineItems:
      cartData.lines?.edges?.map((edge) => ({
        id: edge.node.id,
        quantity: edge.node.quantity,
        title: edge.node.merchandise.product.title,
        variant: {
          id: edge.node.merchandise.id,
          price: edge.node.merchandise.price.amount,
          priceV2: {
            amount: edge.node.merchandise.price.amount,
            currencyCode: edge.node.merchandise.price.currencyCode,
          },
          product: {
            id: edge.node.merchandise.product.id,
            title: edge.node.merchandise.product.title,
            handle: edge.node.merchandise.product.handle,
          },
          image: {
            src:
              edge.node.merchandise.product.images?.edges?.[0]?.node?.url || "",
            altText:
              edge.node.merchandise.product.images?.edges?.[0]?.node?.altText ||
              "",
          },
          selectedOptions: edge.node.merchandise.selectedOptions || [
            { name: "Title", value: "Default Title" },
          ],
        },
      })) || [],
  };

  return transformed;
};

export const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
  const [currentLanguage] = useContext(CurrentLanguage);
  const [client, setClient] = useState(null);
  const [checkout, setCheckout] = useState({ lineItems: [] });
  const [loading, setLoading] = useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Shopify Storefront API client
  useEffect(() => {
    const newClient = createStorefrontApiClient({
      storeDomain,
      apiVersion: "2025-04",
      publicAccessToken: storefrontToken,
    });

    setClient(newClient);
  }, []);

  const setCheckoutItem = (cartData) => {
    const transformedCart = transformCartData(cartData);

    if (isBrowser) {
      localStorage.setItem(localStorageKey, transformedCart.id);
    }
    setCheckout(transformedCart);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeCart = async () => {
      if (!client || !isMounted) return;

      try {
        setLoading(true);

        // Get existing cart ID from localStorage
        const existingCartID = isBrowser
          ? localStorage.getItem(localStorageKey)
          : null;

        let cartData;

        if (existingCartID && existingCartID !== `null`) {
          try {
            const response = await client.request(
              `
                query getCart($id: ID!) {
                  cart(id: $id) {
                    id
                    checkoutUrl
                    cost {
                      subtotalAmount {
                        amount
                        currencyCode
                      }
                      totalAmount {
                        amount
                        currencyCode
                      }
                    }
                    attributes {
                      key
                      value
                    }
                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          merchandise {
                            ... on ProductVariant {
                              id
                              price {
                                amount
                                currencyCode
                              }
                              selectedOptions {
                                name
                                value
                              }
                              product {
                                id
                                title
                                handle
                                images(first: 1) {
                                  edges {
                                    node {
                                      url
                                      altText
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              `,
              {
                variables: {
                  id: existingCartID,
                },
              }
            );

            if (response.data?.cart) {
              const existingCart = response.data.cart;
              const cartLanguage = existingCart.attributes?.find(
                (attr) => attr.key === "language"
              )?.value;

              // If language has changed, create a new cart and transfer items
              if (cartLanguage && cartLanguage !== currentLanguage) {
                const createResponse = await client.request(
                  `
                    mutation cartCreate($input: CartInput!) {
                      cartCreate(input: $input) {
                        cart {
                          id
                          checkoutUrl
                          cost {
                            subtotalAmount {
                              amount
                              currencyCode
                            }
                            totalAmount {
                              amount
                              currencyCode
                            }
                          }
                          attributes {
                            key
                            value
                          }
                          lines(first: 100) {
                            edges {
                              node {
                                id
                                quantity
                                merchandise {
                                  ... on ProductVariant {
                                    id
                                    price {
                                      amount
                                      currencyCode
                                    }
                                    selectedOptions {
                                      name
                                      value
                                    }
                                    product {
                                      id
                                      title
                                      handle
                                      images(first: 1) {
                                        edges {
                                          node {
                                            url
                                            altText
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                        userErrors {
                          code
                          field
                          message
                        }
                      }
                    }
                  `,
                  {
                    variables: {
                      input: {
                        attributes: [
                          { key: "language", value: currentLanguage },
                        ],
                        lines: existingCart.lines.edges.map((edge) => ({
                          merchandiseId: edge.node.merchandise.id,
                          quantity: edge.node.quantity,
                        })),
                      },
                    },
                  }
                );

                if (createResponse.data?.cartCreate?.cart) {
                  cartData = createResponse.data.cartCreate.cart;
                }
              } else {
                cartData = existingCart;
              }
            }
          } catch (e) {
            console.log("Error fetching existing cart:", e);
            localStorage.removeItem(localStorageKey);
          }
        }

        // If no existing cart, create a new one
        if (!cartData) {
          const response = await client.request(
            `
              mutation cartCreate($input: CartInput!) {
                cartCreate(input: $input) {
                  cart {
                    id
                    checkoutUrl
                    cost {
                      subtotalAmount {
                        amount
                        currencyCode
                      }
                      totalAmount {
                        amount
                        currencyCode
                      }
                    }
                    attributes {
                      key
                      value
                    }
                    lines(first: 100) {
                      edges {
                        node {
                          id
                          quantity
                          merchandise {
                            ... on ProductVariant {
                              id
                              price {
                                amount
                                currencyCode
                              }
                              selectedOptions {
                                name
                                value
                              }
                              product {
                                id
                                title
                                handle
                                images(first: 1) {
                                  edges {
                                    node {
                                      url
                                      altText
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  userErrors {
                    code
                    field
                    message
                  }
                }
              }
            `,
            {
              variables: {
                input: {
                  attributes: [{ key: "language", value: currentLanguage }],
                },
              },
            }
          );

          if (response.data?.cartCreate?.cart) {
            cartData = response.data.cartCreate.cart;
          }
        }

        if (isMounted && cartData) {
          setCheckoutItem(cartData);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Cart initialization error:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeCart();

    return () => {
      isMounted = false;
    };
  }, [client, currentLanguage]);

  const addVariantToCart = async (variantId, quantity) => {
    if (!isInitialized) {
      console.error("Cannot add to cart: store not initialized");
      return;
    }

    if (!client || !checkout?.id) {
      console.error("Cannot add to cart: client or checkout not available");
      return;
    }

    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                id
                checkoutUrl
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: checkout.id,
            lines: [
              {
                merchandiseId: variantId,
                quantity: parseInt(quantity, 10),
              },
            ],
          },
        }
      );

      if (response.data?.cartLinesAdd?.cart) {
        setCheckoutItem(response.data.cartLinesAdd.cart);
        setDidJustAddToCart(true);
        setTimeout(() => setDidJustAddToCart(false), 3000);
        return transformCartData(response.data.cartLinesAdd.cart);
      }

      if (response.data?.cartLinesAdd?.userErrors?.length > 0) {
        console.error("Cart errors:", response.data.cartLinesAdd.userErrors);
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeLineItem = async (cartID, lineItemID) => {
    if (!isInitialized) return;

    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
              cart {
                id
                checkoutUrl
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cartID,
            lineIds: [lineItemID],
          },
        }
      );

      if (response.data?.cartLinesRemove?.cart) {
        setCheckoutItem(response.data.cartLinesRemove.cart);
        return transformCartData(response.data.cartLinesRemove.cart);
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateLineItem = async (cartID, lineItemID, quantity) => {
    if (!isInitialized) return;

    setLoading(true);

    try {
      const response = await client.request(
        `
          mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
                checkoutUrl
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                lines(first: 100) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            images(first: 1) {
                              edges {
                                node {
                                  url
                                  altText
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cartID,
            lines: [
              {
                id: lineItemID,
                quantity: parseInt(quantity, 10),
              },
            ],
          },
        }
      );

      if (response.data?.cartLinesUpdate?.cart) {
        setCheckoutItem(response.data.cartLinesUpdate.cart);
        return transformCartData(response.data.cartLinesUpdate.cart);
      }
    } catch (error) {
      console.error("Error updating cart item:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const setValue = (value) => {
    if (isBrowser) {
      localStorage.setItem("rinck_customerAccessToken", JSON.stringify(value));
    }
  };

  const getCustomerAccessToken = () => {
    if (isBrowser) {
      return JSON.parse(localStorage.getItem("rinck_customerAccessToken"));
    }
    return null;
  };

  const defaultValues = {
    client,
    loading,
    isInitialized,
    checkout,
    customerAccessToken: getCustomerAccessToken(),
    didJustAddToCart,
    setValue,
    addVariantToCart,
    removeLineItem,
    updateLineItem,
  };

  return (
    <StoreContext.Provider value={defaultValues}>
      {children}
    </StoreContext.Provider>
  );
};
